import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { notifyUserError, notifyUserSuccess } from 'utils/notifications';
import {
  getClientsFiltersObject,
  getLsUserRoleObject,
  setClientsFiltersObject
} from 'modules/networkTools/localStorageTokens';

import {
  CitiesAutocompleteResponse,
  getAllCHWs,
  getAllManagers,
  getBillingProviders,
  getCHW,
  getCHWsForFilters,
  getCitiesAutocomplete,
  getClient,
  getClientHistory,
  getClientProfileImg,
  getClientsList,
  getClientsStatuses,
  getInsurancePlans,
  getInsurancesAutocomplete,
  getLanguagesAutocomplete,
  getMetrics,
  getMpi,
  getPrograms,
  getProgramsForFilters,
  getStatesAutocomplete,
  getZipCodesAutocomplete,
  InsurancesAutocompleteResponse,
  LanguagesAutocompleteResponse,
  postCreateClient,
  postUpdateManagerReview,
  postUpdateNewManagerReview,
  postUpdateSnapshot,
  postUploadProfileImg,
  putChangeStatus,
  putClientsFavourite,
  putEditClient,
  RaceType,
  StatesAutocompleteResponse,
  ZipCodesAutocompleteResponse
} from './clientsActions';

export interface ClientsContentItem {
  aiScribeEnabled: boolean;
  id: number;
  prependedId: number;
  name: string;
  dateOfBirth: string;
  referral: boolean;
  status: string;
  chwName: string;
  programName: string;
  favourite: boolean;
  startDate: string | null;
  graduationDate: string | null;
  managerReview: string | null;
  supervisorReview: {
    interval: number;
    intervalType: 'WEEK' | 'MONTH';
  };
  lastContact: string | null;
  engagement: {
    interval: number;
    intervalType: 'WEEK' | 'MONTH';
  };
  mpi: boolean | null;
  roadmaps: number | null;
  dateEligible: string | null;
  dateEligibleAsTimestamp?: string | null;
  reEligibility?: {
    available?: boolean;
    date?: string | null;
    programName?: string | null;
  };
  hospitalizationDate?: string | null;
  hospitalizationLocation?: string | null;
  outpatientVisitDate?: string | null;
  outpatientVisitLocation?: string | null;
  clientIdentifier?: string | null;
  primaryLanguage?: string | null;
  primaryPhoneNumber?: string | null;
}

export type StatusId =
  | 'ENROLLED'
  | 'PENDING'
  | 'PRE_OUTREACH'
  | 'TEXT_RESPONSE_PENDING'
  | 'PENDING_CHW'
  | 'OPT_OUT'
  | 'NOT_REACHED'
  | 'WITHDRAWN'
  | 'LOST_TO_FOLLOW_UP'
  | 'GRADUATED'
  | 'TERMINATED_FOR_SAFETY'
  | 'DECEASED'
  | 'DECLINED';

type Priority = 'MAIN' | 'SECONDARY';

export const noteTypeData = [
  { id: 'CHART_REVIEW', name: 'Chart Audit' },
  { id: 'END_OF_LIFE', name: 'End of Life' },
  { id: 'ENROLLMENT', name: 'Enrollment' },
  { id: 'ENROLLMENT_CHILD', name: 'Enrollment - Child' },
  { id: 'ENROLLMENT_TEEN', name: 'Enrollment - Teen' },
  { id: 'FOLLOW_UP', name: 'Follow-up' },
  { id: 'GRADUATION', name: 'Graduation' },
  { id: 'HEALTH_GOAL', name: 'Health Goal' },
  { id: 'HOSPITALIZATION', name: 'Hospitalization' },
  { id: 'MEET_THE_PERSON_INTERVIEW', name: 'Meet the Person Interview' },
  { id: 'MEET_THE_PERSON_INTERVIEW_CHILD', name: 'Meet the Person Interview - Child' },
  { id: 'MEET_THE_PERSON_INTERVIEW_TEEN', name: 'Meet the Person Interview - Teen' },
  { id: 'MID_PREGNANCY_FOLLOW_UP', name: 'Mid-Pregnancy Follow-up' },
  { id: 'POST_DELIVERY', name: 'Post-Delivery' },
  { id: 'PROVIDER_APPOINTMENT', name: 'Provider Appointment' },
  { id: 'SAFETY_NOTE', name: 'Safety Note' }
];

export const clientReachedData = [
  { id: 'YES_AT_HOME', name: 'Yes, in person at home/community' },
  { id: 'YES_AT_MEDICAL_SETTING', name: 'Yes, in person at a medical setting' },
  { id: 'YES_NOT_IN_PERSON', name: 'Yes, but not in person' },
  { id: 'NO', name: 'No' }
];

export const typesOfSupportData = [
  { id: 'NONE', name: 'None' },
  { id: 'SOCIAL_SUPPORT', name: 'Social support' },
  { id: 'COACHING_FOR_HEALTH_HABITS', name: 'Coaching for health habits' },
  { id: 'NAVIGATION_FOR_HEALTHCARE', name: 'Navigation for healthcare' },
  { id: 'NAVIGATION_FOR_MENTAL_HEALTHCARE', name: 'Navigation for mental healthcare' },
  { id: 'ADVOCACY_FOR_EMPOWERMENT', name: 'Advocacy for empowerment' },
  {
    id: 'ACTIVITIES_OF_DAILY_LIVING',
    name: 'Activities of daily living',
    category: 'PROBLEM_SOLVING'
  },
  { id: 'BENEFITS_AND_INSURANCE', name: 'Benefits & insurance', category: 'PROBLEM_SOLVING' },
  { id: 'CAREGIVING', name: 'Caregiving', category: 'PROBLEM_SOLVING' },
  { id: 'CHILDCARE', name: 'Childcare', category: 'PROBLEM_SOLVING' },
  { id: 'CLOTHING_AND_GOODS', name: 'Clothing & goods', category: 'PROBLEM_SOLVING' },
  { id: 'COMMUNITY_GROUPS', name: 'Community groups', category: 'PROBLEM_SOLVING' },
  { id: 'EDUCATION', name: 'Education', category: 'PROBLEM_SOLVING' },
  { id: 'EMPLOYMENT', name: 'Employment', category: 'PROBLEM_SOLVING' },
  { id: 'FINANCIAL_STABILITY', name: 'Financial stability', category: 'PROBLEM_SOLVING' },
  { id: 'FOOD', name: 'Food', category: 'PROBLEM_SOLVING' },
  { id: 'HOUSING', name: 'Housing & Home Repair', category: 'PROBLEM_SOLVING' },
  {
    id: 'INCARCERATION_OR_REENTRY',
    name: 'Incarceration or reentry',
    category: 'PROBLEM_SOLVING'
  },
  { id: 'INTIMATE_PARTNER_SAFETY', name: 'Intimate partner safety', category: 'PROBLEM_SOLVING' },
  { id: 'LEGAL', name: 'Legal', category: 'PROBLEM_SOLVING' },
  { id: 'PHONE_AND_INTERNET', name: 'Phone & internet', category: 'PROBLEM_SOLVING' },
  { id: 'SUBSTANCE_ABUSE', name: 'Substance abuse', category: 'PROBLEM_SOLVING' },
  { id: 'TRANSPORTATION', name: 'Transportation', category: 'PROBLEM_SOLVING' },
  { id: 'UTILITIES', name: 'Utilities', category: 'PROBLEM_SOLVING' }
];

export const whoDidYouReachData = [
  { id: 'CLIENT', name: 'Client or client representative' },
  { id: 'FAMILY_OR_FRIEND', name: 'Family or Friend' },
  { id: 'PROVIDER', name: 'Provider' },
  { id: 'OTHER_CARE_PROFESSIONAL', name: 'Other Care Professional' },
  { id: 'NO_ONE', name: 'No One' }
];

export const whoWereYouTryingToReachData = [
  { id: 'CLIENT', name: 'Client or client representative' },
  { id: 'FAMILY_OR_FRIEND', name: 'Family or Friend' },
  { id: 'PROVIDER', name: 'Provider' },
  { id: 'OTHER_CARE_PROFESSIONAL', name: 'Other Care Professional' },
  {
    id: 'WORK_ON_MY_CLIENTS_BEHALF',
    name: "No one, I was just doing some work on my client's behalf"
  }
];

export const howDidYouReachThemViewOnlyData = [{ id: 'NOT_IN_PERSON', name: 'Not in person' }];

export const howDidYouReachThemData = [
  { id: 'IN_PERSON', name: 'In person' },
  { id: 'PHONE', name: 'Phone' },
  { id: 'VIDEO', name: 'Video' },
  { id: 'TEXT', name: 'Text' },
  { id: 'EMAIL', name: 'Email' },
  ...howDidYouReachThemViewOnlyData
];

export const whatTypeOfPlaceWereYouInViewOnlyData = [
  { id: 'HOME_OR_COMMUNITY', name: 'Home or community' },
  { id: 'MEDICAL_SETTING', name: 'Medical setting' }
];

export const whatTypeOfPlaceWereYouInData = [
  { id: 'CLIENT_HOME', name: 'Client home' },
  { id: 'EMERGENCY_DEPARTMENT', name: 'Emergency department' },
  { id: 'HOSPITAL', name: 'Hospital' },
  { id: 'MEDICAL_OFFICE', name: 'Medical office' },
  { id: 'OTHER_IN_THE_COMMUNITY', name: 'Other (In the community)' },
  ...whatTypeOfPlaceWereYouInViewOnlyData
];

export type Status = {
  id: StatusId;
  name: string;
  priority: Priority;
  description: string;
  category: string;
  chwRequired?: boolean;
};

type Author = {
  id: number;
  name: string;
  profileImageKey: string;
};

export type CareGap = {
  id: string;
  category: string;
  lastUpdated: string;
};

export type Client = {
  snapshot: string;
  managerReview: {
    lastReviewDate: string | null;
    dateAndTimeOfReview?: string | null;
    authorImage: string | null;
    authorName: string | null;
  };
  enableBilling: boolean;
  enableProgramSites: boolean;
  aiScribeEnabled: boolean;
  medicalVisitsEditingAllowed?: boolean;
  medicalVisitsEnabled?: boolean;
  id?: string;
  prependedId: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  dob: string;
  clientIdentifier?: string;
  language?: string;
  genderIdentity?: string;
  races?: RaceType[];
  genderSelfDescribed?: string;
  pronouns?: string;
  pronounsSelfDescribed?: string;
  insuranceId?: string;
  insurancePlan?: {
    id: number;
    name: string;
  };
  primaryPhoneNumber: string;
  secondaryPhoneNumber?: string;
  profileImageKey?: string;
  primaryAddress: {
    streetAddress: string;
    streetAddress2?: string;
    city: string;
    state: string;
    zipCode: string;
    building: string;
    floor: string;
    room: string;
  };
  sameAsPrimaryAddress: boolean;
  currentLocation: {
    streetAddress?: string;
    streetAddress2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    building?: string;
    floor?: string;
    room?: string;
  };
  emergencyContacts: {
    name?: string;
    phoneNumber?: string;
    relationship?: string;
    relationshipOther?: string;
  }[];
  insurances: {
    insuranceCategoryId?: string;
    insuranceCategoryName?: string;
    insurancePlan?: string;
    insuranceNumber?: string;
  }[];
  supportPeople: string | null;
  careTeam: string | null;
  programId: string | null;
  chwId: string | null;
  chwName?: string | null;
  programName?: string | null;
  referralClient: boolean;
  referralDate?: string;
  referredBy?: string;
  referrerPhone?: string;
  referrerEmail?: string;
  referrerNotes?: string;
  eligibleStatuses: Status[];
  status: Status;
  safetyFlag?: boolean;
  reEligibility?: {
    available?: boolean;
    date?: string | null;
    programName?: string | null;
  };
  careGaps: CareGap[];
};

export type ClientHistoryItem = {
  status: Status;
  programName: string;
  changedAt: string;
  chwName: string;
  author: Author;
  reEligibility?: {
    available?: boolean;
    date?: string | null;
    programName?: string | null;
  };
};

type NoteType = {
  id: string;
  displayName: string;
};
export interface StatusesItem {
  id: StatusId;
  name: string;
  category: string;
  noteConfig: {
    availableNoteTypes: NoteType[];
    noteTriggeredOnStatusSelection: boolean;
    preselectedNoteType: NoteType;
  };
}

export interface ProgramsItem {
  id: number;
  name: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  eligibleStatuses: string[];
  valid: boolean;
  active: boolean;
}

export interface CHWItem {
  userId: number;
  organizationId: number;
  name: string;
  profileImageKey: string;
  active: boolean;
}

export interface ClientsSliceProps {
  clientProfileAccordionState: {
    clientId: string;
    accordionOpen: Record<string, boolean>;
  };
  notes: {
    currentNoteID: any | null;
  };
  bulkClientUpload: {
    bulkClientUploadUUID: string | null;
    bulkClientUploadOrgId: string | null;
    fileName: string | null;
  };
  filters: FilterState;
  searchText: string;
  clientsList: {
    content: ClientsContentItem[];
    page: number;
    size: number;
    totalNumber: number;
    percentageOfTotal: number | null;
  };
  clientsListLoading: boolean;
  statusesList: StatusesItem[];
  metricsList: any[];
  programsList: ProgramsItem[];
  chwList: CHWItem[];
  programsListForFilters: ProgramsItem[];
  chwListForFilters: CHWItem[];
  client: {
    loading: boolean;
    data: Client | null;
  };
  clientHistory: {
    content: ClientHistoryItem[];
    page: number;
    size: number;
    totalNumber: number;
  };
  citiesAutocomplete: {
    loading: boolean;
    data: CitiesAutocompleteResponse;
  };
  statesAutocomplete: {
    loading: boolean;
    data: StatesAutocompleteResponse;
  };
  zipCodesAutocomplete: {
    loading: boolean;
    data: ZipCodesAutocompleteResponse;
  };
  languagesAutocomplete: {
    loading: boolean;
    data: LanguagesAutocompleteResponse;
  };
  insurancesAutocomplete: {
    loading: boolean;
    data: InsurancesAutocompleteResponse;
  };
  changeStatusModal: null | 'changeStatus' | 'confirm';
  managers: {
    loading: boolean;
    data: {
      id: number;
      name: string;
      active: boolean;
    }[];
  };
  chws: {
    loading: boolean;
    data: {
      id: number;
      name: string;
      active: boolean;
    }[];
  };
  billingProviders: {
    loading: boolean;
    data: {
      id: number;
      name: string;
    }[];
  };
  insurancePlans: {
    loading: boolean;
    data: {
      id: number;
      name: string;
    }[];
  };
  mpi: {
    isNoteCompleted: boolean;
    isScreenerCompleted: boolean;
    isSnapshotCompleted: boolean;
  };
}

export const defaultClientProfileAccordionState = {
  first: true,
  second: false,
  third: false,
  fourth: false,
  fifth: false
};

export const getFilters = (): FilterState => {
  const userRoleData = getLsUserRoleObject();
  const filters = getClientsFiltersObject();
  if (!filters || filters.length === 0) {
    return {
      statuses: [],
      programIds: [],
      chwIds: [],
      action: null,
      page: 0
    };
  }
  const found = filters.find((a: any) => a.userId === userRoleData.userId);

  return found
    ? { ...found.filters, page: 0 }
    : {
        statuses: [],
        programIds: [],
        chwIds: [],
        action: null,
        page: 0
      };
};

const initialState: ClientsSliceProps = {
  clientProfileAccordionState: {
    clientId: '',
    accordionOpen: defaultClientProfileAccordionState
  },
  notes: {
    currentNoteID: null
  },
  bulkClientUpload: {
    bulkClientUploadUUID: null,
    bulkClientUploadOrgId: null,
    fileName: null
  },
  filters: getFilters() ?? {
    statuses: [],
    programIds: [],
    chwIds: [],
    action: null,
    page: 0
  },
  searchText: '',
  clientsList: {
    content: [],
    page: 0,
    size: 10,
    totalNumber: 0,
    percentageOfTotal: null
  },
  clientsListLoading: true,
  statusesList: [],
  metricsList: [],
  programsList: [],
  programsListForFilters: [],
  chwList: [],
  chwListForFilters: [],
  client: {
    loading: false,
    data: null
  },
  clientHistory: {
    content: [],
    page: 0,
    size: 99999,
    totalNumber: 0
  },
  citiesAutocomplete: {
    loading: false,
    data: []
  },
  statesAutocomplete: {
    loading: false,
    data: []
  },
  zipCodesAutocomplete: {
    loading: false,
    data: []
  },
  languagesAutocomplete: {
    loading: false,
    data: []
  },
  insurancesAutocomplete: {
    loading: false,
    data: []
  },
  changeStatusModal: null,
  managers: {
    loading: false,
    data: []
  },
  chws: {
    loading: false,
    data: []
  },
  billingProviders: {
    loading: false,
    data: []
  },
  insurancePlans: {
    loading: false,
    data: []
  },
  mpi: {
    isNoteCompleted: false,
    isScreenerCompleted: false,
    isSnapshotCompleted: false
  }
};

// Filter-related types moved from clientsOld.tsx
export type Action = 'RUN_THE_LIST' | 'ENGAGE' | 'MPI' | 'OUTREACH' | 'SUPPORT';

export type FilterState = {
  statuses: string[];
  programIds: string[];
  chwIds: string[];
  action: Action | null;
  page: number;
};

export type FilterAction =
  | {
      type: 'SET_STATUS' | 'SET_PROGRAM' | 'SET_CHW';
      payload: string[];
    }
  | {
      type: 'SET_ACTION';
      payload: Action | null;
    }
  | {
      type: 'UPDATE';
      payload: Partial<FilterState>;
    };

// Filters reducer function moved from clientsOld.tsx
export const filtersReducer = (state: FilterState, action: FilterAction): FilterState => {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, statuses: action.payload };
    case 'SET_PROGRAM':
      return { ...state, programIds: action.payload };
    case 'SET_CHW':
      return { ...state, chwIds: action.payload };
    case 'SET_ACTION':
      return { ...state, action: action.payload };
    case 'UPDATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

export const clientsSlice = createSlice({
  name: 'clientsSlice',
  initialState,
  reducers: {
    resetAccordionState: (state, action) => {
      (state.clientProfileAccordionState.accordionOpen as any) = defaultClientProfileAccordionState;
    },
    setClientListPage: (state, action) => {
      (state.clientsList.page as any) = action.payload;
    },
    updateAccordionState: (state, action) => {
      (state.clientProfileAccordionState.accordionOpen as any)[action.payload.currentAccordionTab] =
        action.payload.currentAccordionValue;
    },
    setBulkClientUploadUUID: (state, action) => {
      state.bulkClientUpload.bulkClientUploadUUID = action.payload.bulkClientUploadUUID;
      state.bulkClientUpload.bulkClientUploadOrgId = action.payload.organizationId;
      state.bulkClientUpload.fileName = action.payload.fileName;
    },
    setNotesCurrentID: (state, action) => {
      state.notes.currentNoteID = action.payload.currentNoteID;
    },
    setNewClientPhoto: (state, action) => {
      if (state.client.data) {
        state.client.data.profileImageKey = action.payload.profileImageKey;
      }
    },
    setChangeStatusModal: (state, action: PayloadAction<'changeStatus' | 'confirm' | null>) => {
      state.changeStatusModal = action.payload;
    },
    resetClientHistory: (state) => {
      state.clientHistory = {
        content: [],
        page: 0,
        size: 99999,
        totalNumber: 0
      };
    },
    setFiltersStatus: (state, action: PayloadAction<string[]>) => {
      state.filters.statuses = action.payload;
    },
    setFiltersProgram: (state, action: PayloadAction<string[]>) => {
      state.filters.programIds = action.payload;
    },
    setFiltersCHW: (state, action: PayloadAction<string[]>) => {
      state.filters.chwIds = action.payload;
    },
    setFiltersAction: (state, action: PayloadAction<Action | null>) => {
      state.filters.action = action.payload;
    },
    updateFilters: (state, action: PayloadAction<Partial<FilterState>>) => {
      const userRoleData = getLsUserRoleObject();
      const newFilters = { ...state.filters, ...action.payload };
      setClientsFiltersObject([{ filters: newFilters, userId: userRoleData.userId }]);
      state.filters = newFilters;
    },
    initializeFilters: (state) => {
      const filters = getFilters();
      state.filters = filters;
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    clearSearchText: (state) => {
      state.searchText = '';
    },
    clearInsurancePlans: (state) => {
      state.insurancePlans.data = [];
    },
    resetClient: (state) => {
      state.client = {
        loading: false,
        data: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClientsList.pending, (state: any) => {
        state.clientsListLoading = true;
      })
      .addCase(getClientsList.fulfilled, (state: any, { payload }: any) => {
        state.clientsList = payload;
        state.clientsListLoading = false;
      })
      .addCase(getClientsList.rejected, (state: any, { payload }: any) => {
        state.clientsListLoading = false;
        notifyUserError(payload);
      })
      .addCase(getClientsStatuses.fulfilled, (state: any, { payload }: any) => {
        state.statusesList = payload;
      })
      .addCase(getClientsStatuses.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getMetrics.fulfilled, (state: any, { payload }: any) => {
        state.metricsList = payload;
      })
      .addCase(getMetrics.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getPrograms.fulfilled, (state: any, { payload }: any) => {
        state.programsList = payload;
      })
      .addCase(getPrograms.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getCHW.fulfilled, (state: any, { payload }: any) => {
        state.chwList = payload.content;
      })
      .addCase(getCHW.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putClientsFavourite.pending, (state: any, { meta }) => {
        // Optimistically update the UI so the change is instant
        const filteredList = state.clientsList.content.map((item: ClientsContentItem) => {
          if (item.id === meta.arg.id) {
            return { ...item, favourite: meta.arg.isFavourite };
          }
          return item;
        });
        state.clientsList = { ...state.clientsList, content: filteredList };
      })
      .addCase(putClientsFavourite.rejected, (state: any, { meta, payload }) => {
        // Revert the optimistic update in case of error
        const filteredList = state.clientsList.content.map((item: ClientsContentItem) => {
          if (item.id === meta.arg.id) {
            return { ...item, favourite: !meta.arg.isFavourite };
          }
          return item;
        });
        state.clientsList = { ...state.clientsList, content: filteredList };
        notifyUserError(payload as any);
      })
      .addCase(postCreateClient.fulfilled, (state, { payload }) => {
        state.client.data = payload;
        notifyUserSuccess('Success! New client created.');
      })
      .addCase(postCreateClient.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putEditClient.fulfilled, (state, { payload, meta }) => {
        state.client.data = {
          ...payload,
          clientIdentifier: meta.arg.client.clientIdentifier,
          profileImageKey: state?.client?.data?.profileImageKey
        };
        notifyUserSuccess('Success! Client successfully updated.');
      })
      .addCase(putEditClient.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putChangeStatus.fulfilled, (state, { payload }) => {
        if (payload.status?.id !== state.client.data?.status?.id) {
          notifyUserSuccess('Status successfully updated!');
        } else {
          notifyUserSuccess('Client successfully updated!');
        }
        if (state.client.data) {
          state.client.data = {
            ...state.client.data,
            ...payload
          };
        }
      })
      .addCase(putChangeStatus.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getClient.pending, (state, { meta }) => {
        if (!meta.arg.skipLoading) {
          state.client.loading = true;
        }
      })
      .addCase(getClient.fulfilled, (state, { payload }: any) => {
        const selfDescribed = payload.data?.races?.find(
          (race: RaceType) => race?.id === 'SELF_DESCRIBED'
        );
        if (selfDescribed) {
          payload.data.selfDescribed = selfDescribed?.name;
        }
        state.client = { loading: false, data: payload.data };
      })
      .addCase(getClient.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getClientHistory.fulfilled, (state, { payload }: any) => {
        state.clientHistory = payload;
      })
      .addCase(getClientHistory.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      // Cities
      .addCase(getCitiesAutocomplete.pending, (state) => {
        state.citiesAutocomplete = {
          data: [],
          loading: true
        };
      })
      .addCase(getCitiesAutocomplete.fulfilled, (state, action) => {
        state.citiesAutocomplete = {
          data: action.payload,
          loading: false
        };
      })
      .addCase(getCitiesAutocomplete.rejected, (state, payload: any) => {
        notifyUserError(payload);
      })
      // States
      .addCase(getStatesAutocomplete.pending, (state) => {
        state.statesAutocomplete = {
          data: [],
          loading: true
        };
      })
      .addCase(getStatesAutocomplete.fulfilled, (state, action) => {
        state.statesAutocomplete = {
          data: action.payload,
          loading: false
        };
      })
      .addCase(getStatesAutocomplete.rejected, (_, payload: any) => {
        notifyUserError(payload);
      })
      // ZIP Codes
      .addCase(getZipCodesAutocomplete.pending, (state) => {
        state.zipCodesAutocomplete = {
          data: [],
          loading: true
        };
      })
      .addCase(getZipCodesAutocomplete.fulfilled, (state, action) => {
        state.zipCodesAutocomplete = {
          data: action.payload,
          loading: false
        };
      })
      .addCase(getZipCodesAutocomplete.rejected, (_, payload: any) => {
        notifyUserError(payload);
      })
      // Languages
      .addCase(getLanguagesAutocomplete.pending, (state) => {
        state.languagesAutocomplete = {
          data: [],
          loading: true
        };
      })
      .addCase(getLanguagesAutocomplete.fulfilled, (state, action) => {
        state.languagesAutocomplete = {
          data: action.payload,
          loading: false
        };
      })
      .addCase(getLanguagesAutocomplete.rejected, (_, payload: any) => {
        notifyUserError(payload);
      })
      // Insurances
      .addCase(getInsurancesAutocomplete.pending, (state) => {
        state.insurancesAutocomplete = {
          data: [],
          loading: true
        };
      })
      .addCase(getInsurancesAutocomplete.fulfilled, (state, action) => {
        state.insurancesAutocomplete = {
          data: action.payload,
          loading: false
        };
      })
      .addCase(getInsurancesAutocomplete.rejected, (_, payload: any) => {
        notifyUserError(payload);
      })
      .addCase(postUpdateSnapshot.fulfilled, (state, { meta }) => {
        if (state.client.data) {
          state.client.data.snapshot = meta.arg.snapshot;
          state.mpi = { ...state.mpi, isSnapshotCompleted: Boolean(meta.arg.snapshot) };
        }
      })
      .addCase(postUpdateSnapshot.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(postUploadProfileImg.fulfilled, (state, { payload }) => {
        notifyUserSuccess('Image uploaded successfully.');
        // if (state.client.data) {
        //   state.client.data.profileImageKey = payload;
        // }
      })
      .addCase(postUploadProfileImg.rejected, (_, { payload }: any) => {
        // notifyUserError(payload);
      })

      .addCase(getClientProfileImg.fulfilled, (state: any, { payload }: any) => {
        // state.client.data.profileImageKey = URL.createObjectURL(new Blob([payload]));
      })
      .addCase(getClientProfileImg.rejected, (_, { payload }: any) => {
        // notifyUserError(payload);
      })
      .addCase(postUpdateManagerReview.fulfilled, (state, { payload }) => {
        if (state.client.data) {
          state.client.data.managerReview = payload;
        }

        notifyUserSuccess('Your changes were saved');
      })
      .addCase(postUpdateNewManagerReview.fulfilled, (state, { payload }) => {
        if (state.client.data) {
          state.client.data.managerReview = payload;
        }

        notifyUserSuccess('Your changes were saved');
      })
      .addCase(postUpdateNewManagerReview.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getAllManagers.pending, (state) => {
        state.managers.loading = true;
      })
      .addCase(getAllManagers.fulfilled, (state, { payload }) => {
        state.managers.data = payload;
      })
      .addCase(getAllManagers.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getAllCHWs.pending, (state) => {
        state.chws.loading = true;
      })
      .addCase(getAllCHWs.fulfilled, (state, { payload }) => {
        state.chws.data = payload;
      })
      .addCase(getAllCHWs.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getProgramsForFilters.fulfilled, (state, { payload }) => {
        state.programsListForFilters = payload;
      })
      .addCase(getProgramsForFilters.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getCHWsForFilters.fulfilled, (state, { payload }) => {
        state.chwListForFilters = payload.content;
      })
      .addCase(getCHWsForFilters.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getBillingProviders.pending, (state) => {
        state.billingProviders.loading = true;
      })
      .addCase(getBillingProviders.fulfilled, (state, { payload }) => {
        state.billingProviders.data = payload;
      })
      .addCase(getBillingProviders.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getInsurancePlans.pending, (state) => {
        if (state.insurancePlans?.loading) {
          state.insurancePlans.loading = true;
        }
      })
      .addCase(getInsurancePlans.fulfilled, (state, { payload }) => {
        state.insurancePlans.data = payload;
      })
      .addCase(getMpi.fulfilled, (state, { payload }) => {
        state.mpi = payload;
      })
      .addCase(getMpi.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      });
  }
});
export const {
  resetAccordionState,
  setBulkClientUploadUUID,
  setNewClientPhoto,
  setNotesCurrentID,
  setChangeStatusModal,
  resetClientHistory,
  updateAccordionState,
  setClientListPage,
  setFiltersStatus,
  setFiltersProgram,
  setFiltersCHW,
  setFiltersAction,
  updateFilters,
  initializeFilters,
  setSearchText,
  clearSearchText,
  clearInsurancePlans,
  resetClient
} = clientsSlice.actions;
export default clientsSlice.reducer;
